/**
 * The feedback page on a phone (#661; the FeedbackPhone board): block by block, in reading order, full width.
 *
 * Each block is the question strip (where a question starts), a crop of the block inside its frame, shown whole with no scrolling (`region-crop.tsx`: a
 * block wider than the phone is scaled to fit as one picture, never panned), and the note under it, joined to its frame by a short vertical line.
 * A note with no frame on the page has no crop and no line, and says why it has none. A line that was only read has no card, as the board draws it:
 * an opened run is frames, each keeping its own "How this was checked" (invariant 3).
 *
 * "See the whole page" opens the full viewer (zoom and pan, `feedback-viewer.tsx`) with every frame and a numbered mark on it. A mark opens its note
 * as a bottom sheet: it has a close control, closes on Escape and on a press outside it, and keeps the focus inside while it is open; closing returns
 * the focus to the mark. The tab bar stays.
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useHomeworkLabels } from '../../i18n/homework-labels.js';
import { useMessages } from '../../i18n/locale.js';
import { createPortal } from 'react-dom';
import { useFeedbackLabels } from '../../i18n/homework-feedback-labels.js';
import { AlsoToLook, needsALook } from './feedback-also.js';
import { frameOf, onlyRead, questionGroup, questionHasGuess, stripStarts, type BoardNote } from './feedback-blocks.js';
import { feedbackChrome } from './feedback-chrome.js';
import { frameOutline, kindGlyph, PageFrames, QuestionBand, useNoteWords } from './feedback-frames.js';
import { FeedbackPageImage } from './feedback-page-image.js';
import type { FeedbackPagesProps } from './feedback-pages.js';
import { RegionCrop, type Bbox } from './region-crop.js';
import { UntrustedText } from './untrusted-text.js';
import { PageViewer } from './feedback-viewer.js';

/** A line of a block of several lines is drawn about this tall, in CSS px; a block of one line is drawn at the crop's own readable size (44). */
const LINE_HEIGHT_PX = 40;

/**
 * The block's crop, readable at a phone's width: a line is about 40 CSS px tall (a single line, 44), and a line too wide for the phone stacks
 * into rows (`region-crop.tsx`) instead of being drawn smaller. `lines` are the boxes of the block's own lines.
 */
export function BlockCrop({ pageId, bbox, lines, lineNumber, widthPx, heightPx, text }: {
  readonly pageId: string;
  readonly bbox: Bbox;
  readonly lines: readonly Bbox[];
  readonly lineNumber: number;
  readonly widthPx: number;
  readonly heightPx: number;
  readonly text: string | undefined;
}): React.JSX.Element {
  const blockHeight = Math.max(1, bbox.y1 - bbox.y0);
  const heights = lines.map((line) => line.y1 - line.y0).filter((height) => height > 0).sort((a, b) => a - b);
  const lineHeight = heights[Math.floor(heights.length / 2)];
  // RegionCrop scales the block so its box is `boxHeightPx` tall: a line's own height decides that, not the whole block's.
  const boxHeightPx = lines.length > 1 && lineHeight !== undefined ? (blockHeight * LINE_HEIGHT_PX) / lineHeight : undefined;
  return (
    <RegionCrop
      pageId={pageId}
      bbox={bbox}
      approximate={false}
      lineNumber={lineNumber}
      widthPx={widthPx}
      heightPx={heightPx}
      text={text}
      fill
      {...(boxHeightPx !== undefined ? { boxHeightPx } : {})}
    />
  );
}

export function PhoneFeedback(props: FeedbackPagesProps & { readonly selectedId: string | undefined; readonly onSelect: (id: string, focus?: boolean) => void }): React.JSX.Element {
  const m = useMessages(feedbackChrome);
  const t = useFeedbackLabels();
  const h = useHomeworkLabels();
  const [whole, setWhole] = useState(false);
  const [wholePage, setWholePage] = useState<string | undefined>(undefined);
  const [sheetId, setSheetId] = useState<string | undefined>(undefined);
  const lastPin = useRef<HTMLElement | null>(null);
  const pageIds = props.pageIds;
  // Opens on the page of the first note that needs a look, as the desktop viewer does; page 1 when there is none.
  const firstLook = pageIds.find((id) => props.notes.some((note) => note.pageId === id && needsALook(note)));
  const current = wholePage !== undefined && pageIds.includes(wholePage) ? wholePage : (firstLook ?? pageIds[0]);
  const currentAt = current === undefined ? 0 : pageIds.indexOf(current);
  const stackRef = useRef<HTMLDivElement | null>(null);
  const pagerRef = useRef<HTMLDivElement | null>(null);
  const refocus = useRef<'previous' | 'next' | undefined>(undefined);
  useEffect(() => {
    if (refocus.current === undefined) return;
    pagerRef.current?.querySelector<HTMLButtonElement>(`[data-side="${refocus.current}"]`)?.focus();
    refocus.current = undefined;
  }, [current]);
  const goTo = (id: string | undefined, side: 'previous' | 'next'): void => {
    if (id === undefined) return;
    setWholePage(id);
    // A button that disables itself on arrival would drop the focus: it moves to the other button, which is always live after a move.
    const arrivedAtEnd = pageIds[pageIds.indexOf(id) + (side === 'next' ? 1 : -1)] === undefined;
    refocus.current = arrivedAtEnd ? (side === 'next' ? 'previous' : 'next') : undefined;
    stackRef.current?.scrollIntoView?.({ block: 'start' });
  };
  const sheetNote = sheetId === undefined ? undefined : props.notes.find((note) => note.id === sheetId);

  const closeSheet = (): void => {
    setSheetId(undefined);
    lastPin.current?.focus();
  };

  return (
    <div className="fb-phone">
      {props.leadRef !== undefined ? <div ref={props.leadRef} className="fb-lead" /> : null}
      <button type="button" className="fb-phone-toggle" aria-pressed={whole} onClick={() => setWhole((now) => !now)}>
        <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="5" y="3.5" width="14" height="17" rx="2" />
          <path d="M9 8h6M9 12h6M9 16h3" />
        </svg>
        {whole ? m.readBlockByBlock : m.seeWholePage}
      </button>

      {whole ? (
        <div className="fb-phone-whole">
          {pageIds.map((pageId) => {
            const page = props.pageOf(pageId);
            if (pageId !== current || page === undefined) return null;
            const at = pageIds.indexOf(pageId);
            const entries = props.notes.filter((note) => note.pageId === pageId);
            return (
              <section key={pageId} className="fb-page" data-current="true" aria-labelledby={`feedback-page-${pageId}`}>
                <h2 id={`feedback-page-${pageId}`} className="visually-hidden">
                  {h.pageN(props.pageNumberOf(pageId))}
                </h2>
                <QuestionBand notes={entries} />
                <PageViewer
                  current
                  pageId={pageId}
                  pageNumber={props.pageNumberOf(pageId)}
                  totalPages={props.totalPages}
                  thumbs={pageIds.map((id) => ({ id, number: props.pageNumberOf(id) }))}
                  previousId={pageIds[at - 1]}
                  nextId={pageIds[at + 1]}
                  onGo={setWholePage}
                  widthPx={page.widthPx}
                  heightPx={page.heightPx}
                >
                  <FeedbackPageImage pageId={pageId} pageNumber={props.pageNumberOf(pageId)} widthPx={page.widthPx} heightPx={page.heightPx} />
                  <PageFrames
                    widthPx={page.widthPx}
                    heightPx={page.heightPx}
                    notes={entries}
                    selectedId={sheetId}
                    showNumbers
                    onSelect={(id) => {
                      lastPin.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
                      setSheetId(id);
                    }}
                  />
                </PageViewer>
                {entries.some((note) => note.blockBbox !== null) ? <p className="fb-frame-words">{t.frameWords}</p> : null}
              </section>
            );
          })}
          {sheetNote !== undefined ? <NoteSheet note={sheetNote} onClose={closeSheet} render={(note) => props.renderNote(note, { selected: true, onSelect: () => undefined, bare: true })} /> : null}
        </div>
      ) : (
        <>
          {pageIds.length > 1 ? (
            <div ref={pagerRef} className="fb-phone-pager">
              <button type="button" className="fb-phone-pager-btn" data-side="previous" aria-label={m.previousPage} disabled={pageIds[currentAt - 1] === undefined} onClick={() => goTo(pageIds[currentAt - 1], 'previous')}>
                <span aria-hidden="true" className="fb-arrow">‹</span>
              </button>
              <p className="fb-phone-pager-text">{m.pageOf(props.pageNumberOf(current ?? ''), props.totalPages)}</p>
              <button type="button" className="fb-phone-pager-btn" data-side="next" aria-label={m.nextPage} disabled={pageIds[currentAt + 1] === undefined} onClick={() => goTo(pageIds[currentAt + 1], 'next')}>
                <span aria-hidden="true" className="fb-arrow">›</span>
              </button>
            </div>
          ) : null}
          <AlsoToLook notes={props.notes} pageIds={pageIds} current={current} pageNumberOf={props.pageNumberOf} onGo={setWholePage} />
          <BlockStack {...props} page={current} stackRef={stackRef} />
        </>
      )}
      {props.showPostFixed && !whole ? (
        <Link className="fb-post-fixed" to="/homework">
          {m.postFixed}
        </Link>
      ) : null}
    </div>
  );
}

/** The blocks of the page shown, in reading order. */
function BlockStack(props: FeedbackPagesProps & { readonly selectedId: string | undefined; readonly onSelect: (id: string, focus?: boolean) => void; readonly page: string | undefined; readonly stackRef: React.RefObject<HTMLDivElement | null> }): React.JSX.Element {
  const m = useMessages(feedbackChrome);
  const t = useFeedbackLabels();
  const wordsOf = useNoteWords();
  const placed = props.notes.filter((note) => note.pageId === props.page);
  // One page shows at a time, so each says its own question, also one that continues from the page before.
  const strips = stripStarts(placed, true);
  // Lines read and not checked are quiet: a run of them (in one question) is one thin row, opened in place, so a page that was mostly read is not a
  // scroll of near-identical cards. A ✓, a ✗, a flag and everything else that says something of its own shows its crop and note at once.
  const [openRuns, setOpenRuns] = useState<ReadonlySet<string>>(new Set());
  const quiet = onlyRead;
  const runStartOf = (index: number): boolean => {
    const note = placed[index] as BoardNote;
    const before = placed[index - 1];
    return quiet(note) && (before === undefined || !quiet(before) || strips.has(note.id) || before.pageId !== note.pageId);
  };
  const runIdOf = (index: number): string => {
    let at = index;
    while (at > 0 && !runStartOf(at)) at -= 1;
    return (placed[at] as BoardNote).id;
  };
  // "Most of this page was read" is said at the top of each page shown, the first time that page is quiet.
  const mostlyQuiet = placed.filter(quiet).length * 2 > placed.length;
  return (
    <div ref={props.stackRef} className="fb-phone-stack-wrap">
    {placed.length === 0 ? <p className="fb-phone-lead">{m.noNotesOnPage}</p> : null}
    <ol className="fb-phone-stack plain-list" aria-label={m.blocksList}>
      {placed.map((note, index) => {
        const page = props.pageOf(note.pageId);
        const box = note.blockBbox;
        const { tag } = wordsOf(note);
        const reading = note.members.flatMap((member) => member.readings)[0]?.text;
        const runStart = runStartOf(index);
        const runId = quiet(note) ? runIdOf(index) : undefined;
        const runOpen = runId !== undefined && openRuns.has(runId);
        const hidden = runId !== undefined && !runOpen;
        const framedQuiet = quiet(note) && box !== null;
        const runAnchor = runStart ? `feedback-run-${runId ?? ''}` : undefined;
        if (hidden && !runStart && !strips.has(note.id)) return null;
        let runLabels: BoardNote[] = [];
        if (runStart) {
          for (let at = index; at < placed.length && quiet(placed[at] as BoardNote) && runIdOf(at) === runId; at += 1) runLabels = [...runLabels, placed[at] as BoardNote];
        }
        return (
          <li key={note.id} className="fb-phone-item" data-kind={note.kind}>
            {strips.has(note.id) && note.questionLabel !== null ? (
              <p className="fb-phone-strip">
                <span className="fb-strip-label">
                  {m.questionStrip(questionGroup(note.questionLabel) ?? note.questionLabel)}
                  {questionHasGuess(placed, note.questionLabel) ? ` · ${m.ourGuess}` : ''}
                </span>
                {note.questionText === undefined ? null : <span dir="auto" className="fb-strip-text"><UntrustedText text={note.questionText} /></span>}
              </p>
            ) : null}
            {index === 0 && mostlyQuiet ? <p className="fb-phone-lead">{m.mostReadNotChecked}</p> : null}
            {index === 0 && placed.some((entry) => entry.blockBbox !== null) ? <p className="fb-frame-words">{t.frameWords}</p> : null}
            {runStart && runId !== undefined ? (
              <button
                type="button"
                className="fb-phone-run"
                aria-expanded={runOpen}
                aria-controls={`feedback-run-${runId}`}
                onClick={() => setOpenRuns((now) => (now.has(runId) ? new Set([...now].filter((id) => id !== runId)) : new Set([...now, runId])))}
              >
                <span aria-hidden="true" className="fb-phone-run-dot" />
                <span className="fb-phone-run-text">
                  <strong>{t.markWord('read_not_checked')}</strong>
                  {[...new Set(runLabels.map((entry) => entry.questionLabel).filter((label): label is string => label !== null))].map((label, at) => (
                    <span key={label}>
                      {at === 0 ? ' · ' : ', '}
                      <UntrustedText text={label} />
                    </span>
                  ))}
                </span>
                <span aria-hidden="true" className="fb-phone-run-chevron" data-open={runOpen} />
              </button>
            ) : null}
            {hidden ? null : box !== null && page !== undefined ? (
              <div id={runAnchor} className="fb-phone-block">
                <div className="fb-phone-frame" data-kind={note.kind} data-outline={frameOutline(note.kind)} data-selected={note.id === props.selectedId}>
                  <span className="fb-frame-tag">{tag}</span>
                  <span className="fb-badge-dot fb-phone-badge" aria-hidden="true">
                    {kindGlyph(note)}
                  </span>
                  <BlockCrop
                    pageId={note.pageId}
                    bbox={box}
                    lines={note.members.flatMap((member) => (frameOf(member) === null ? [] : [frameOf(member) as Bbox]))}
                    lineNumber={note.number}
                    widthPx={page.widthPx}
                    heightPx={page.heightPx}
                    text={reading}
                  />
                </div>
                {framedQuiet ? null : <span className="fb-phone-conn" data-kind={note.kind} aria-hidden="true" />}
              </div>
            ) : null}
            {hidden ? null : <div id={box === null ? runAnchor : undefined} className="fb-phone-note" data-how-only={framedQuiet}>{props.renderNote(note, { howOnly: framedQuiet, selected: note.id === props.selectedId, onSelect: (id) => props.onSelect(id) })}</div>}
          </li>
        );
      })}
    </ol>
    </div>
  );
}

/**
 * A note as a sheet over the page. A dialog, so the keyboard has a way in and out: the focus moves to the close control, Tab stays inside, and
 * Escape or a press on the dimmed page closes it.
 */
function NoteSheet({ note, onClose, render }: { readonly note: BoardNote; readonly onClose: () => void; readonly render: (note: BoardNote) => React.JSX.Element }): React.JSX.Element {
  const m = useMessages(feedbackChrome);
  const wordsOf = useNoteWords();
  const sheet = useRef<HTMLDivElement | null>(null);
  const close = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    close.current?.focus();
  }, [note.id]);
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab' || sheet.current === null) return;
    const focusable = [...sheet.current.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], summary, [tabindex]:not([tabindex="-1"])')];
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (first === undefined || last === undefined) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  const { name } = wordsOf(note);
  return (
    createPortal(
    <>
      <button type="button" className="fb-scrim" aria-label={m.closeNote} tabIndex={-1} onClick={onClose} />
      <div ref={sheet} className="fb-sheet-modal" role="dialog" aria-modal="true" aria-label={m.noteSheetLabel(name)} onKeyDown={onKeyDown}>
        <span aria-hidden="true" className="fb-sheet-handle" />
        <div className="feedback-page">{render(note)}</div>
        <button ref={close} type="button" className="fb-sheet-close" onClick={onClose}>
          {m.closeNote}
        </button>
      </div>
    </>,
    document.body,
    )
  );
}
